import { useState, useEffect, useRef } from "react";
import "./RicInputBox.css"
import { ReactComponent as ClearIcon } from "../../../assets/icons/close-line.svg";
import { getFlagSvg } from "../Flag/Flag";
import { getSearchResult } from "../../../api/search.api";

interface OptionType {
    value: string;
    label: string;
    [key: string]: any; // Allow additional properties
}

interface InputBoxProps {
    placeholder?: string; // Optional placeholder text
    initialValue?: string; // Optional initial value for the input
    onChange?: (value: string) => void; // Optional callback for value changes
    onSelect?: (value: string) => void; // Optional callback when an option is selected
    onSelectOption?: (option: any) => void; // Optional callback with full option object
    onClear?: () => void; // Optional callback when the clear button is clicked
    debounceMs?: number; // Debounce delay in milliseconds
    minQueryLength?: number; // Minimum query length before making API call
    loading?: boolean; // External loading state
    token?: string; // Token for API call
    region?: string; // Region for API call
    sector?: string; // Sector for API call
    resetTrigger?: number; // External trigger to reset the input value
}

// Custom debounce hook for input values
const useDebouncedValue = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const RicInputBox = ({
    placeholder,
    initialValue = '',
    onChange,
    onSelect,
    onSelectOption,
    onClear,
    debounceMs = 300,
    minQueryLength = 1,
    loading = false,
    token,
    region,
    sector,
    resetTrigger,
}: InputBoxProps) => {
    const [value, setValue] = useState<string>(initialValue);
    const [options, setOptions] = useState<OptionType[]>([{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }]);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const lastSelectedValueRef = useRef<string>('');
    const isClearingRef = useRef<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const debouncedValue = useDebouncedValue(value, debounceMs);

    // Handle external reset trigger
    useEffect(() => {
        if (resetTrigger !== undefined) {
            // handleClear()
        }
    }, [resetTrigger]);

    // Handle API calls when debounced value changes
    useEffect(() => {
        // Skip API call if the debounced value matches the last selected value
        const isValueFromSelection = debouncedValue === lastSelectedValueRef.current;

        if (debouncedValue.length >= minQueryLength && !isValueFromSelection) {
            setIsLoading(true);
            handleSearch(debouncedValue)
                .then((results) => {
                    setOptions(results);
                    setIsOpen(true);
                    setSelectedIndex(-1);
                })
                .catch((error) => {
                    console.error('API call failed:', error);
                    setOptions([]);
                    setIsOpen(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else if (debouncedValue.length < minQueryLength) {
            setOptions([]);
            setIsOpen(false);
        }

        // Clear the last selected value after processing to allow future API calls
        if (isValueFromSelection) {
            lastSelectedValueRef.current = '';
        }
    }, [debouncedValue, minQueryLength]);

    const handleSearch = async (keyword: string) => {
        const res = await getSearchResult({ keyword: keyword, page: 1, size: 10, token: token || "", region: region, sector: sector });
        if (res.result) {
            res.result.forEach((item: any) => {
                //add Flag svg to html property
                item.html =
                    <div className="input-box-dropdown-item-content" >
                        <div className="input-box-dropdown-item-content-text">
                            <div className="input-box-dropdown-item-content-text-symbol">{item.symbol}</div>
                            <div className="input-box-dropdown-item-content-text-name">{item.name}</div>
                        </div>
                        <div className="input-box-dropdown-item-content-flag">{getFlagSvg(item.countryCode)}
                        </div>
                    </div>;
            });
            return res.result;
        }
        return [];
    };

    // Handle input changes
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
    };

    // Handle clear button click
    const handleClear = () => {
        isClearingRef.current = true; // Set flag to prevent focus from opening dropdown
        setValue('');
        setOptions([]);
        setIsOpen(false);
        setSelectedIndex(-1);
        if (onChange) {
            onChange('');
        }
        inputRef.current?.focus();
        if (onClear) {
            onClear();
        }
        // Reset the clearing flag after a short delay
        setTimeout(() => {
            isClearingRef.current = false;
        }, 100);
    };

    // Handle option selection
    const handleOptionSelect = (option: OptionType) => {
        const selectedValue = option.symbol;
        lastSelectedValueRef.current = selectedValue; // Store the selected value to prevent API call
        setValue(selectedValue);
        setIsOpen(false);
        setSelectedIndex(-1);
        if (onSelect) {
            onSelect(selectedValue);
        }
        if (onSelectOption) {
            onSelectOption(option);
        }
        if (onChange) {
            onChange(selectedValue);
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || options.length === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setSelectedIndex(prev =>
                    prev < options.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                event.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < options.length) {
                    handleOptionSelect(options[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Handle input focus
    const handleFocus = () => {
        // Don't open dropdown if we're currently clearing
        if (isClearingRef.current) {
            return;
        }
        // Only open dropdown if there are options and the input has content
        if (options.length > 0 && value.length >= minQueryLength) {
            setIsOpen(true);
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="input-box body-m-regular" ref={dropdownRef}>
            <input
                ref={inputRef}
                type="text"
                className="text"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                autoComplete="off"
            />
            {value && ( // Only show the clear button if there's text in the input
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClear();
                    }}
                    className="btn"
                    aria-label="Clear input"
                >
                    {/* Cross icon for clearing */}
                    <ClearIcon className="icon" />
                </button>
            )}

            {/* Dropdown with options */}
            {isOpen && (
                <div className="input-box-dropdown">
                    <div className="input-box-dropdown-container">
                        {isLoading || loading ? (
                            <div className="input-box-dropdown-item loading">
                                Loading...
                            </div>
                        ) : options.length > 0 ? (
                            options.map((option, index) => (
                                <div
                                    key={option.symbol || index}
                                    className={`input-box-dropdown-item ${index === selectedIndex ? 'selected' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOptionSelect(option);
                                    }}
                                >
                                    {option.html}
                                </div>
                            ))
                        ) : debouncedValue.length >= minQueryLength ? (
                            <div className="input-box-dropdown-item no-results">
                                No results found
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RicInputBox